import {
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import useMeasure from 'react-use/esm/useMeasure';

import { Tooltip, makeStyles } from '@material-ui/core';
import type { TooltipProps } from '@material-ui/core/Tooltip';

export type RichOverflowTooltipProps = {
  title?: TooltipProps['title'];
  line?: number;
  placement?: TooltipProps['placement'];
  children: ReactNode;
};

type StyleProps = { line: number };

const useStyles = makeStyles({
  truncate: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: ({ line }: StyleProps) => (line > 1 ? '-webkit-box' : 'block'),
    whiteSpace: ({ line }: StyleProps) => (line > 1 ? 'normal' : 'nowrap'),
    '-webkit-line-clamp': ({ line }: StyleProps) => line,
    '-webkit-box-orient': 'vertical',
    minWidth: 0,
  },
});

export const RichOverflowTooltip = ({
  title,
  line = 1,
  placement,
  children,
}: RichOverflowTooltipProps) => {
  const classes = useStyles({ line });
  const [measureRef, { width, height }] = useMeasure<HTMLDivElement>();
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [derivedTitle, setDerivedTitle] = useState<string>('');

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      elementRef.current = el;
      measureRef(el as HTMLDivElement);
    },
    [measureRef],
  );

  useLayoutEffect(() => {
    const el = elementRef.current;
    if (!el) return;
    setIsOverflowing(
      line > 1
        ? el.scrollHeight > el.clientHeight
        : el.scrollWidth > el.clientWidth,
    );
    if (title === undefined) {
      setDerivedTitle(el.textContent ?? '');
    }
  }, [width, height, children, title, line]);

  const tooltipTitle = isOverflowing ? (title ?? derivedTitle) : '';

  return (
    <Tooltip title={tooltipTitle} placement={placement}>
      <div ref={setRef} className={classes.truncate}>
        {children}
      </div>
    </Tooltip>
  );
};
